/**
 * Ações do guia que mexem em outras partes do painel (janela de captura, janela da extensão,
 * registro de uso). Ficam fora dos componentes para o convite, o cartão e o controle usarem as
 * mesmas regras.
 */
import { useCaptureStore } from '../../stores/captureStore';
import { useSessionStore } from '../../stores/sessionStore';
import { useUiStore } from '../../stores/uiStore';
import { EXTENSION_MODAL_ID } from '../../hooks/useRequireExtension';
import { can } from '../../lib/entitlements';
import { emitActivity } from '../../lib/activity';
import { useGuiaStore, type GuiaOrigem, type GuiaSalvo } from './guiaStore';
import type { OnboardingResult } from '../onboarding/OnboardingDialog';
import type { GuiaModulo, PassoId } from './passos';

/**
 * Abre a busca do tipo escolhido, na janela, e vai ao primeiro passo que cabe: a licença vencida
 * (não dá para capturar), a extensão que falta (a janela de instalação, que é da ferramenta, por
 * cima) ou os campos.
 */
export function abrirBuscaDoGuia(modulo: GuiaModulo, patch: Partial<GuiaSalvo> = {}): void {
  const guia = useGuiaStore.getState();
  const session = useSessionStore.getState().session;
  const capture = useCaptureStore.getState();
  const dados = { ...patch, modulo, inicioBusca: null, listaId: null, contatos: 0 };
  if (!can(session, 'capture.enabled')) {
    guia.ir('licenca', dados);
    return;
  }
  capture.closeCategories();
  capture.openCaptureWindow(modulo, { janela: true });
  if (capture.extensionPresent === false) {
    guia.ir('extensao', dados);
    useUiStore.getState().openModal(EXTENSION_MODAL_ID);
    return;
  }
  guia.ir('campo_termo', dados);
}

/** Reabre a janela de captura do guia (fechada no meio ou para ajustar a busca). */
export function reabrirBusca(passo: PassoId = 'campo_termo'): void {
  const guia = useGuiaStore.getState();
  if (!guia.modulo) return;
  useCaptureStore.getState().openCaptureWindow(guia.modulo, { janela: true });
  guia.ir(passo);
}

/** Passo alcançado, para o funil do guia (proposta, seção 8). */
export function registrarPasso(passo: PassoId, modulo: GuiaModulo | null): void {
  emitActivity('guide.step_reached', { step: passo, ...(modulo ? { module: modulo } : {}) });
}

/** A pessoa abriu a lista da busca: o guia termina (pedido do Marcelo, 28/10/2026). */
export function concluirGuia(listaId: string): void {
  const guia = useGuiaStore.getState();
  emitActivity('guide.completed', {
    ...(guia.modulo ? { module: guia.modulo } : {}),
    contacts: guia.contatos,
  });
  guia.ir('fim', { listaId });
}

/** Começa (ou recomeça) o guia, na escolha da busca. */
export function comecarGuia(origem: GuiaOrigem, perfil?: OnboardingResult | null): void {
  useGuiaStore.getState().comecar(origem, perfil);
  emitActivity('guide.started', {
    source: origem === 'convite' ? 'invite' : origem === 'inicio' ? 'home_empty' : 'menu',
  });
}
